import React, { useMemo } from 'react';
import { AppManagerPrivateAPI } from '../appManagerAPI';
import { INTERACTIONS } from '../constants/constants';
import { DealerSegmentApp } from '../services/dealer/dealer.segmentApps';
import { DealerAppPageData } from '../services/dealer/dealer.types';
import {
  DealerSegmentContent,
  loadSegmentContent,
} from '../services/dealer/dealer.segmentContent';
import { UseRequestResponse, useRequest } from '../hooks/useRequest';
import { V2App } from '@wix/ambassador-app-service-webapp/types';

interface AppManagerData {
  segmentApps: UseRequestResponse<DealerSegmentApp[]>;
  dealerData: UseRequestResponse<Record<string, DealerAppPageData>>;
  marketApps: UseRequestResponse<V2App[] | undefined>;
  segmentContent: UseRequestResponse<DealerSegmentContent | undefined>;
}

export interface IAppManagerDataContextInput {
  children?: React.ReactNode;
  api: AppManagerPrivateAPI;
  locale: string;
}

export interface IAppManagerDataContextValue {
  api: AppManagerPrivateAPI;
  data: AppManagerData;
}

export const AppManagerDataContext =
  React.createContext<IAppManagerDataContextValue>(
    {} as IAppManagerDataContextValue,
  );

export const AppManagerDataProvider: React.FC<IAppManagerDataContextInput> = ({
  children,
  api,
  locale,
}) => {
  const segmentApps = useRequest(() => api.loadAndTransformSegmentApps(), {
    fedopsInteractionName: INTERACTIONS.APP_MANAGER.FETCH_DEALER_SEGMENT_APPS,
    initialData: [],
  });

  const dealerData = useRequest(() => api.loadAndTransformAppPageOverrides(), {
    fedopsInteractionName: INTERACTIONS.APP_MANAGER.FETCH_DEALER_INFO,
    initialData: {},
  });

  const marketApps = useRequest(() => api.queryMarketListingApps(locale), {
    fedopsInteractionName: INTERACTIONS.APP_MANAGER.FETCH_WIX_APPS,
    initialData: [],
  });

  const segmentContent = useRequest(
    () => loadSegmentContent(api.metaSiteInstance),
    {
      fedopsInteractionName:
        INTERACTIONS.APP_MANAGER.FETCH_DEALER_SEGMENT_CONTENT,
      initialData: {},
    },
  );

  const data: AppManagerData = useMemo(
    () => ({
      segmentApps,
      dealerData,
      marketApps,
      segmentContent,
    }),
    [segmentApps, dealerData, marketApps, segmentContent],
  );

  const appManagerData = useMemo(
    () => ({
      api,
      data,
    }),
    [data, api],
  );

  return (
    <AppManagerDataContext.Provider value={appManagerData}>
      {children}
    </AppManagerDataContext.Provider>
  );
};
